/**
 * Route table for the authenticated area (rendered inside <AppShell/> by App.js).
 * Order matters for sibling patterns (`/pools/from-estimate/...` before `/pools/:poolId`).
 * `roles` is an allow-list enforced by <RequireAuth/>; omitted → any signed-in user.
 * Phase 56 (committee R-5): every page is code-split with React.lazy so the public landing page ships without the
 * dashboards, charts and PDF screens; App.js wraps the router in <Suspense/>.
 */
import { lazy } from "react";
import { Navigate } from "react-router-dom";

const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const PoolsPage = lazy(() => import("@/pages/PoolsPage"));
const PoolDetailPage = lazy(() => import("@/pages/PoolDetailPage"));
const PoolFromEstimatePage = lazy(() => import("@/pages/PoolFromEstimatePage"));
const EstimatesPage = lazy(() => import("@/pages/EstimatesPage"));
const ValuatorRoundsPage = lazy(() => import("@/pages/ValuatorRoundsPage"));
const ValuationRoundPage = lazy(() => import("@/pages/ValuationRoundPage"));
const TransfersPage = lazy(() => import("@/pages/TransfersPage"));
const TransactionsPage = lazy(() => import("@/pages/TransactionsPage"));
const DecisionsPage = lazy(() => import("@/pages/DecisionsPage"));
const RecordsPage = lazy(() => import("@/pages/RecordsPage"));
const AdminUsersPage = lazy(() => import("@/pages/AdminUsersPage"));
const AdminLandingPage = lazy(() => import("@/pages/AdminLandingPage"));
const AdminHealthPage = lazy(() => import("@/pages/AdminHealthPage"));
const AdminDataPage = lazy(() => import("@/pages/AdminDataPage"));
const AdminCompliancePage = lazy(() => import("@/pages/AdminCompliancePage"));
const InvitationsPage = lazy(() => import("@/pages/InvitationsPage"));
const WalletPage = lazy(() => import("@/pages/WalletPage"));
const LedgerExplorerPage = lazy(() => import("@/pages/LedgerExplorerPage"));
const ProfilePage = lazy(() => import("@/pages/ProfilePage"));
const OrganizationPage = lazy(() => import("@/pages/OrganizationPage"));
const AdminOrganizationsPage = lazy(() => import("@/pages/AdminOrganizationsPage"));

// Role allow-lists (module constants so the `roles` prop keeps a stable identity across renders).
export const ADMIN_ONLY = ["admin"];
export const MANAGER_OR_ADMIN = ["pool_manager", "admin"];
export const MANAGER_ONLY = ["pool_manager"];
export const VALUATION_ROLES = ["valuator", "admin", "pool_manager"];
export const OWNER_ONLY = ["owner"];

/** Old bookmarks / notifications pointing at the retired exit-requests page land on the Transactions hub (Phase 45 → 49). */
const RedemptionsRedirect = () => <Navigate to="/transactions" replace />;

export const SHELL_ROUTES = [
  { path: "/dashboard", Page: DashboardPage },
  { path: "/pools", Page: PoolsPage },
  { path: "/pools/from-estimate/:estimateId?", Page: PoolFromEstimatePage, roles: MANAGER_ONLY },
  { path: "/estimates", Page: EstimatesPage, roles: MANAGER_OR_ADMIN },
  { path: "/pools/:poolId", Page: PoolDetailPage },
  { path: "/valuations", Page: ValuatorRoundsPage, roles: VALUATION_ROLES },
  { path: "/valuations/:roundId", Page: ValuationRoundPage },
  { path: "/decisions", Page: DecisionsPage, roles: OWNER_ONLY }, // Phase 46: votes due first, then every decision across my holdings
  { path: "/transactions", Page: TransactionsPage }, // Phase 49: one hub — Transfer shares · Move an asset · Liquidate an asset (tabs)
  { path: "/transfers", Page: TransfersPage }, // Phase 49: the old sidebar address redirects into the hub's first tab
  { path: "/records", Page: RecordsPage, roles: OWNER_ONLY }, // Phase 46: statements & documents · distributions · audit record (owners; other roles keep /ledger)
  { path: "/redemptions", Page: RedemptionsRedirect }, // Phase 45/46: the retired "Exit requests" page simply lands on Transfers (legacy exit-window UI removed)
  { path: "/admin/users", Page: AdminUsersPage, roles: ADMIN_ONLY },
  { path: "/admin/organizations", Page: AdminOrganizationsPage, roles: ADMIN_ONLY },
  { path: "/organization", Page: OrganizationPage, roles: MANAGER_ONLY },
  { path: "/admin/landing", Page: AdminLandingPage, roles: ADMIN_ONLY },
  { path: "/admin/health", Page: AdminHealthPage, roles: ADMIN_ONLY },
  { path: "/admin/data", Page: AdminDataPage, roles: ADMIN_ONLY },
  { path: "/admin/compliance", Page: AdminCompliancePage, roles: ADMIN_ONLY },
  { path: "/invitations", Page: InvitationsPage, roles: MANAGER_OR_ADMIN },
  { path: "/wallet", Page: WalletPage },
  { path: "/ledger", Page: LedgerExplorerPage },
  { path: "/profile", Page: ProfilePage },
];
